/**
 * 蓝图工作卡(BPWB-1,2026-09-03)—— 追爆蓝图落到各目的地创建页时的统一形制。
 *
 * ═══ 为什么把细条换成卡 ═══════════════════════════════════════════════════════
 * 五个去处(视频/口播/官媒/组图/长图)原来各有一根「已套用蓝图「X」」细条,页面主体
 * 仍是各舱通用创建页 —— 用户反馈「点过去之后还在我们首页,没有明显的标志」。
 * 这张卡把三样立起来:①承诺句(照它的什么、换成你的什么)②结构可视化(镜头轨/
 * 节拍轨/写法格,由目的地用插槽填)③原片本身(有留存就能当场播,看得见照着拍的是什么)。
 * 设计稿:console `_design-mock/dept-video-create-blueprint.html`。
 *
 * ═══ 分工 ═════════════════════════════════════════════════════════════════════
 * 本卡只管**形制与原片播放**;档位切换、警示条、步骤卡都是目的地自己的领域内容,
 * 从 `children` / `pledge` / `structure` 插进来 —— 不在这里长业务分支(五方 if 会把它
 * 变回收敛前的 VideoSourceBar)。
 *
 * ★原片直链:点击才签(30min TTL,提前签=会过期的空头支票);URL 只进 <video src>
 *   不 fetch(CSP);404 人话原样转达。与 BlueprintDetailSheet 同口径。
 */
import { Flame, Loader2, PlayCircle, RefreshCcw, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import { ApiError } from "@/shared/api";
import { PLATFORM_LABEL } from "@/shared/lib/platformLabel";
import { Button } from "@/shared/ui/button";

import { blueprintMediaService } from "../api/blueprintMediaService";

interface Props {
  formulaName: string;
  sourcePlatform?: string | null;
  /** 头行主句;缺省「已套用蓝图「{formulaName}」」。 */
  title?: ReactNode;
  /** 有原片(source.rawVideoKey 有值)→ 左列竖屏播放位;blueprintId 用来签直链。 */
  blueprintId?: number | null;
  hasRawVideo?: boolean;
  /** 承诺句:照它的什么、换成你的什么(青底行)。 */
  pledge?: ReactNode;
  /** 结构可视化插槽(镜头轨 / 节拍轨 / 写法格…)。 */
  structure?: ReactNode;
  /** 卡内追加行(档位切换、警示条等目的地领域内容)。 */
  children?: ReactNode;
  /** 紧凑态(编排后的幕二等):收掉播放位与结构插槽,只留头行 + children。 */
  compact?: boolean;
  disabled?: boolean;
  onSwap?: () => void;
  onClear?: () => void;
}

export function BlueprintHeroCard({
  formulaName,
  sourcePlatform,
  title,
  blueprintId,
  hasRawVideo,
  pledge,
  structure,
  children,
  compact,
  disabled,
  onSwap,
  onClear,
}: Props) {
  const platform = sourcePlatform ? (PLATFORM_LABEL[sourcePlatform] ?? sourcePlatform) : null;
  const [rawUrl, setRawUrl] = useState<string | null>(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawError, setRawError] = useState<string | null>(null);

  const loadRawVideo = async () => {
    if (!blueprintId) return;
    setRawLoading(true);
    setRawError(null);
    try {
      const res = await blueprintMediaService.getRawVideoUrl(blueprintId);
      if (typeof res?.url === "string" && res.url) setRawUrl(res.url);
      else setRawError("没取到原片地址,稍后再试一次。");
    } catch (e) {
      setRawError(e instanceof ApiError ? e.message : "原片暂时打不开,稍后再试一次。");
    } finally {
      setRawLoading(false);
    }
  };

  const showPlayer = !compact && hasRawVideo && !!blueprintId;

  return (
    <div
      data-testid="blueprint-hero-card"
      className="rounded-lg border-2 border-primary/60 bg-primary/5 px-3.5 py-3 text-sm"
    >
      <div className={showPlayer ? "flex flex-col gap-3 sm:flex-row" : undefined}>
        {showPlayer && (
          <div className="w-full shrink-0 sm:w-32">
            {rawUrl ? (
              // 别人的爆款原片,手里没有字幕轨可挂(同 BlueprintDetailSheet 口径)
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                data-testid="hero-raw-video"
                src={rawUrl}
                controls
                playsInline
                preload="metadata"
                className="max-h-[300px] w-full rounded-md border border-border bg-black"
              />
            ) : (
              <button
                type="button"
                data-testid="hero-raw-video-load"
                disabled={rawLoading}
                onClick={() => void loadRawVideo()}
                className="flex aspect-[9/16] max-h-[300px] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-border bg-muted/60 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none"
              >
                {rawLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <PlayCircle className="h-6 w-6" />
                )}
                <span>播放爆款原片</span>
              </button>
            )}
            {rawError && (
              <p data-testid="hero-raw-video-error" className="mt-1 text-2xs text-destructive">
                {rawError}
              </p>
            )}
            {rawUrl && (
              <button
                type="button"
                onClick={() => void loadRawVideo()}
                className="mt-1 cursor-pointer text-2xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
              >
                播放地址 30 分钟有效,过期点这里重新取链
              </button>
            )}
          </div>
        )}

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Flame className="h-4 w-4 shrink-0 text-primary" />
            <span className="min-w-0">
              {title ?? (
                <>
                  已套用蓝图<b>「{formulaName}」</b>
                </>
              )}
              {platform && (
                <span className="ml-1.5 text-xs text-muted-foreground">来自 {platform}</span>
              )}
            </span>
            <div className="flex-1" />
            {onSwap && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 gap-1"
                disabled={disabled}
                onClick={onSwap}
              >
                <RefreshCcw className="h-3 w-3" />
                换蓝图
              </Button>
            )}
            {onClear && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 gap-1 text-muted-foreground"
                disabled={disabled}
                onClick={onClear}
              >
                <X className="h-3 w-3" />
                清除,从零开始
              </Button>
            )}
          </div>

          {pledge && (
            <div className="rounded-md border border-primary/25 bg-primary/10 px-3 py-2 text-[13px] leading-relaxed">
              {pledge}
            </div>
          )}

          {!compact && structure}

          {children}
        </div>
      </div>
    </div>
  );
}
